/* eslint-disable react-hooks/exhaustive-deps */
import React, { Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Center, Spinner } from "@chakra-ui/react";
import Sidebar from "components/sidebar";
import routes from "routes";
import { usePermissions } from "hooks/usePermissions";
import ErrorBoundary from "components/errorBoundary/ErrorBoundary";
import { SUPERUSER_BASE_ROUTE, SUPERUSER_DASHBOARD_ROUTE } from "utils";

export default function SuperUser(props: { [x: string]: any }) {
  const location = useLocation();
  const [open, setOpen] = React.useState(true);
  const [currentRoute, setCurrentRoute] = React.useState("Dashboard");
  const { hasPermission } = usePermissions();

  const defaultRoute = React.useMemo(() => {
    for (const r of routes) {
      if (r.layout === "/superuser") {
        if (!r.children || r.children.length === 0) {
          if (r.sidebar && (!r.module || hasPermission(r.module, "READ"))) {
            if (r.path) return `/superuser/${r.path}`;
          }
        } else {
          for (const child of r.children) {
            if (child.sidebar && (!child.module || hasPermission(child.module, "READ"))) {
              if (child.path) return `/superuser/${child.path}`;
            }
          }
        }
      }
    }
    return SUPERUSER_DASHBOARD_ROUTE;
  }, [hasPermission]);

  const mainContentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleResize = () =>
      window.innerWidth < 1200 ? setOpen(false) : setOpen(true);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  React.useEffect(() => {
    getActiveRoute(routes);
    // Reset scroll positions on route change
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  const getActiveRoute = (routes: any[]): string | boolean => {
    let activeRoute = "Dashboard";

    const findActiveRoute = (nestedRoutes: any[]) => {
      for (let i = 0; i < nestedRoutes.length; i++) {
        const splitPath = location.pathname.split("/");

        if (splitPath.includes(nestedRoutes[i].path)) {
          setCurrentRoute(nestedRoutes[i].name);
          activeRoute = nestedRoutes[i].name;
        }

        if (nestedRoutes[i].children) {
          findActiveRoute(nestedRoutes[i].children);
        }
      }
    };

    findActiveRoute(routes);

    return activeRoute;
  };

  const renderRouteElement = (comp: any) => {
    if (!comp) return null;
    if (React.isValidElement(comp)) {
      return React.cloneElement(comp);
    }
    const Component = comp;
    return <Component />;
  };

  const getRoutes = (routes: any[]): any => {
    return routes.map((prop, key) => {
      if (prop.layout === "/superuser") {
        if (!prop.children || prop.children.length === 0) {
          // Handle routes without children
          return (
            <Route path={prop.path} element={renderRouteElement(prop.component)} key={key} />
          );
        } else {
          // Handle routes with children
          return prop.children.map((childProp: any, childKey: number) => {
            return (
              <Route
                path={childProp.path}
                element={renderRouteElement(childProp.component)}
                key={`${key}-${childKey}`}
              />
            );
          });
        }
      } else {
        return null;
      }
    });
  };

  const isMainDashboard = React.useMemo(() => {
    const normalizedPath = location.pathname.toLowerCase().replace(/\/+$/, "");
    return (
      normalizedPath === SUPERUSER_DASHBOARD_ROUTE ||
      normalizedPath === SUPERUSER_BASE_ROUTE
    );
  }, [location.pathname]);

  const isDevicePage = React.useMemo(() => {
    return location.pathname.includes("device");
  }, [location.pathname]);

  document.documentElement.dir = "ltr";
  return (
    <div className="flex min-h-screen w-full">
      <Sidebar
        open={open}
        handleClick={open ? () => setOpen(false) : () => setOpen(true)}
      />
      {/* Navbar & Main Content */}
      <div className="min-h-screen w-full bg-lightPrimary dark:!bg-navy-900">
        <main
          className={`flex-none transition-all duration-300 ease-in-out ${
            open ? "ml-[260px]" : "ml-[70px]"
          }`}
        >
          {/* Routes */}
          <div
            ref={mainContentRef}
            className={`superuser-content-wrapper ${
              isMainDashboard
                ? "superuser-content-wrapper--dashboard custom-scrollbar"
                : "superuser-content-wrapper--default"
            }`}
          >
            <div
              className={`superuser-content-container ${
                isMainDashboard
                  ? "superuser-content-container--dashboard"
                  : isDevicePage
                  ? "superuser-content-container--device"
                  : "superuser-content-container--default"
              }`}
            >
              <ErrorBoundary>
                <Suspense
                  fallback={
                    <Center h="300px" w="100%">
                      <Spinner size="lg" color="#2ead81" />
                    </Center>
                  }
                >
                  <Routes>
                    {getRoutes(routes)}
                    <Route
                      path="/"
                      element={<Navigate to={defaultRoute} replace />}
                    />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
